// Shared bits for the Integrations surface (ff-api#1232).
import React from 'react';
import {
  ActivityState,
  IntegrationActivity,
  IntegrationAlarm,
  Integration,
  IntegrationValidity,
  QuotaUtilization,
  Severity,
  SpendSource,
} from '@/client/types/integrations';

export const INTEGRATIONS_BASE = '/admin/integrations';

export const fmtNum = (n?: number | null) => (n == null ? '—' : n.toLocaleString());
export const fmtUsd = (n?: number | null) =>
  n == null
    ? '—'
    : `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: n < 1 ? 4 : 2,
      })}`;
export const fmtPct = (n?: number | null) => (n == null ? '—' : `${n.toFixed(1)}%`);
/** Null means nothing was called — say so rather than showing 0%. */
export const fmtErrorRate = (rate?: number | null) =>
  rate == null ? 'no calls' : `${(rate * 100).toFixed(rate < 0.01 && rate > 0 ? 2 : 1)}%`;

export const fmtLatency = (ms?: number | null) =>
  ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;

/** Null is "nobody has said", not zero — the two must not look alike. */
export const fmtMoney = (amount?: number | null, currency?: string | null) =>
  amount == null
    ? 'unknown'
    : `${amount.toLocaleString(undefined, {
        minimumFractionDigits: amount < 1 && amount > 0 ? 4 : 2,
        maximumFractionDigits: amount < 1 && amount > 0 ? 4 : 2,
      })} ${currency ?? ''}`.trim();

const SPEND_SOURCE_LABEL: Record<SpendSource, string> = {
  free: 'free',
  configured_subscription: 'subscription fee',
  llm_ledger: 'LLM ledger',
  metered: 'metered per call',
  balance_drop: 'balance drop',
  configured_per_call: 'calls x unit price',
  unknown: 'not priced',
};

const SPEND_SOURCE_STYLE: Record<SpendSource, string> = {
  free: 'bg-gray-100 text-gray-600',
  configured_subscription: 'bg-blue-50 text-blue-700',
  llm_ledger: 'bg-violet-50 text-violet-700',
  metered: 'bg-green-50 text-green-700',
  balance_drop: 'bg-teal-50 text-teal-700',
  configured_per_call: 'bg-indigo-50 text-indigo-700',
  unknown: 'bg-amber-50 text-amber-700',
};

/** Says where a figure came from, so a guess is never mistaken for an invoice. */
export const SpendSourceBadge: React.FC<{ source: SpendSource }> = ({ source }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      SPEND_SOURCE_STYLE[source] ?? SPEND_SOURCE_STYLE.unknown
    }`}
  >
    {SPEND_SOURCE_LABEL[source] ?? source}
  </span>
);

export const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

/** Worst-first, matching the ops-health ladder. */
export const SEVERITY_ORDER: Severity[] = ['CRITICAL', 'MAJOR', 'MINOR', 'WARNING', 'OK'];

const SEVERITY_STYLE: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 ring-red-200',
  MAJOR: 'bg-orange-100 text-orange-700 ring-orange-200',
  MINOR: 'bg-amber-100 text-amber-700 ring-amber-200',
  WARNING: 'bg-yellow-100 text-yellow-800 ring-yellow-200',
  OK: 'bg-green-100 text-green-700 ring-green-200',
};

const STATUS_STYLE: Record<string, string> = {
  live: 'bg-green-50 text-green-700 ring-green-200',
  placeholder: 'bg-gray-100 text-gray-600 ring-gray-200',
  planned: 'bg-blue-50 text-blue-700 ring-blue-200',
  broken: 'bg-red-50 text-red-700 ring-red-200',
};

export const severityRank = (s?: string) => {
  const i = SEVERITY_ORDER.indexOf(String(s ?? 'OK').toUpperCase() as Severity);
  return i === -1 ? SEVERITY_ORDER.length : i;
};

export const SeverityBadge: React.FC<{ severity?: string; className?: string }> = ({
  severity,
  className = '',
}) => {
  const s = String(severity ?? 'OK').toUpperCase();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        SEVERITY_STYLE[s] ?? SEVERITY_STYLE.OK
      } ${className}`}
    >
      {s}
    </span>
  );
};

export const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const s = String(status ?? 'live');
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        STATUS_STYLE[s] ?? STATUS_STYLE.placeholder
      }`}
    >
      {s}
    </span>
  );
};

/** Worst first: the order of the summary chips and of activity groups. */
export const ACTIVITY_STYLE: Record<ActivityState, string> = {
  failing: 'bg-red-50 text-red-700 ring-red-200',
  degraded: 'bg-orange-50 text-orange-700 ring-orange-200',
  over: 'bg-red-50 text-red-700 ring-red-200',
  idle: 'bg-amber-50 text-amber-700 ring-amber-200',
  underused: 'bg-amber-50 text-amber-700 ring-amber-200',
  active: 'bg-green-50 text-green-700 ring-green-200',
  untracked: 'bg-gray-50 text-gray-600 ring-gray-200',
  off: 'bg-gray-50 text-gray-500 ring-gray-200',
};
export const ACTIVITY_STATES = Object.keys(ACTIVITY_STYLE) as ActivityState[];

/** Working and used, or not, with the reason on hover and for screen readers. */
export const ActivityBadge: React.FC<{ activity?: IntegrationActivity }> = ({ activity }) =>
  activity ? (
    <span
      title={activity.reason}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        ACTIVITY_STYLE[activity.state] ?? ACTIVITY_STYLE.untracked
      }`}
    >
      {activity.state}
      <span className="sr-only">: {activity.reason}</span>
    </span>
  ) : null;

export const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, hint, icon, color }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="truncate text-xl font-semibold text-gray-900">{value}</p>
        {hint && <p className="truncate text-xs text-gray-400">{hint}</p>}
      </div>
    </div>
  </div>
);

/** A quota/ceiling bar. Undefined percentage renders nothing, never a full bar. */
export const UsageBar: React.FC<{ pct?: number | null; label?: string }> = ({ pct, label }) => {
  if (pct == null) return <span className="text-xs text-gray-400">no quota data</span>;
  const clamped = Math.max(0, Math.min(100, pct));
  const tone = clamped >= 98 ? 'bg-red-500' : clamped >= 90 ? 'bg-orange-500' : clamped >= 70 ? 'bg-amber-400' : 'bg-green-500';
  return (
    <div className="min-w-[7rem]">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${clamped}%` }} />
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {fmtPct(pct)}
        {label ? ` ${label}` : ''}
      </p>
    </div>
  );
};

const quotaLabel = (u: QuotaUtilization) =>
  u.source === 'ours' ? `of ${fmtNum(u.limit)} / ${u.period}` : 'of plan';

/** The quota closest to its limit: the one that will bite first. */
export const usagePct = (row: Integration): { pct: number | null; label: string } => {
  const live = (row.utilization ?? []).filter((u) => u.state === 'active' && u.pct != null);
  if (live.length) {
    const worst = live.reduce((a, b) => ((b.pct ?? 0) > (a.pct ?? 0) ? b : a));
    const more = live.length > 1 ? ` · +${live.length - 1} more` : '';
    return { pct: worst.pct ?? null, label: `${quotaLabel(worst)}${more}` };
  }
  const observed = row.observed;
  if (observed?.used_pct != null) return { pct: observed.used_pct, label: 'of plan' };
  const ceilingPct = observed?.self_reported?.ceiling_pct;
  if (ceilingPct != null) return { pct: ceilingPct, label: 'of ceiling' };
  return { pct: null, label: '' };
};

const daysLabel = (days?: number | null) =>
  days == null ? '' : days < 0 ? `${-days}d ago` : days === 0 ? 'today' : `${days}d left`;

/** When the next key, contract or quota lapses. */
export const ValidityCell: React.FC<{ validity?: IntegrationValidity | null }> = ({ validity }) => {
  if (!validity) return <span className="text-xs text-gray-400">no end date</span>;
  const tone =
    validity.days < 0 ? 'text-red-600' : validity.days <= 14 ? 'text-amber-600' : 'text-gray-700';
  return (
    <div className="whitespace-nowrap">
      <p className={`text-sm ${tone}`}>{validity.on}</p>
      <p className="text-xs text-gray-500">
        {validity.kind} · {daysLabel(validity.days)}
      </p>
    </div>
  );
};

/** Every quota for one vendor, each against its own period. */
export const UtilizationTable: React.FC<{ rows?: QuotaUtilization[] }> = ({ rows }) => {
  if (!rows?.length) {
    return (
      <p className="text-sm text-gray-400">
        No quota recorded. Add one under Configuration to track usage against it.
      </p>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500">
          <th className="py-1 pr-3 font-medium">Quota</th>
          <th className="py-1 pr-3 font-medium">Period</th>
          <th className="py-1 pr-3 font-medium">Used</th>
          <th className="py-1 pr-3 font-medium">Utilization</th>
          <th className="py-1 font-medium">Valid</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((u, i) => (
          <tr key={i} className="border-t border-gray-50 align-top">
            <td className="py-2 pr-3 text-gray-900">
              {u.label}
              <p className="text-xs text-gray-500">{u.source === 'ours' ? 'ours' : u.source}</p>
            </td>
            <td className="py-2 pr-3 text-gray-600">{u.period}</td>
            <td
              className="py-2 pr-3 text-gray-700"
              title={
                u.basis === 'observed'
                  ? 'Every outbound call we recorded in this period'
                  : u.basis === 'billed'
                    ? 'Billable calls metered this month'
                    : undefined
              }
            >
              {fmtNum(u.used)} / {fmtNum(u.limit)}
            </td>
            <td className="py-2 pr-3">
              {u.state === 'active' ? (
                <>
                  <UsageBar pct={u.pct} />
                  {u.elapsed_pct != null && (
                    <p className="text-xs text-gray-400">{fmtPct(u.elapsed_pct)} of period gone</p>
                  )}
                </>
              ) : (
                <span className={u.state === 'expired' ? 'text-xs text-red-600' : 'text-xs text-gray-500'}>
                  {u.state}
                </span>
              )}
            </td>
            <td className="whitespace-nowrap py-2 text-gray-600">
              {u.from ?? '…'} → {u.until ?? 'open'}
              {u.days_left != null && (
                <p className="text-xs text-gray-500">{daysLabel(u.days_left)}</p>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const alarmName = (alarm: IntegrationAlarm) =>
  String(alarm.component ?? '').startsWith('integration:')
    ? String(alarm.component).slice('integration:'.length)
    : String(alarm.component ?? '—');

/** Windows the traffic/error views can be read over. */
export const WINDOWS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

export const WindowPicker: React.FC<{ days: number; onChange: (d: number) => void }> = ({
  days,
  onChange,
}) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
    {WINDOWS.map((w) => (
      <button
        key={w.days}
        type="button"
        onClick={() => onChange(w.days)}
        className={`px-3 py-1.5 text-xs font-medium ${
          days === w.days ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {w.label}
      </button>
    ))}
  </div>
);

export const Pagination: React.FC<{
  page: number;
  pages: number;
  total: number;
  perPage: number;
  onPage: (p: number) => void;
  onPerPage: (n: number) => void;
}> = ({ page, pages, total, perPage, onPage, onPerPage }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-sm">
    <span className="text-gray-500">
      {total === 0
        ? 'Nothing matches these filters'
        : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} of ${total}`}
    </span>
    <div className="flex items-center gap-2">
      <select
        value={perPage}
        onChange={(e) => onPerPage(Number(e.target.value))}
        className="h-8 rounded-lg border border-gray-200 px-2 text-xs outline-none focus:border-blue-400"
      >
        {[25, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n} per page
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="rounded-lg border border-gray-200 px-2 py-1 text-xs disabled:opacity-40"
      >
        Previous
      </button>
      <span className="text-xs text-gray-500">
        {page} / {pages}
      </span>
      <button
        type="button"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
        className="rounded-lg border border-gray-200 px-2 py-1 text-xs disabled:opacity-40"
      >
        Next
      </button>
    </div>
  </div>
);

/** A recorded failure, shown as one line plus the vendor's own message. */
export const ErrorSampleRow: React.FC<{
  sample: { ts: string; provider: string; outcome: string; status?: number | null;
            method?: string | null; path?: string | null; message?: string | null;
            latency_ms?: number | null };
  showProvider?: boolean;
}> = ({ sample, showProvider }) => (
  <li className="border-b border-gray-50 py-2 last:border-0">
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="rounded bg-red-50 px-1.5 py-0.5 font-medium text-red-700">
        {sample.status ?? sample.outcome}
      </span>
      {showProvider && <span className="font-medium text-gray-800">{sample.provider}</span>}
      <span className="text-gray-500">
        {sample.method} {sample.path ?? '—'}
      </span>
      <span className="ml-auto text-gray-400">{fmtDate(sample.ts)}</span>
    </div>
    {sample.message && (
      <p className="mt-1 break-all font-mono text-xs text-gray-700">{sample.message}</p>
    )}
  </li>
);

export const PageShell: React.FC<{
  title: string;
  description: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, actions, children }) => (
  <div className="min-h-screen bg-gray-50 p-6">
    <div className="mx-auto max-w-screen-2xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">{description}</p>
        </div>
        {actions}
      </div>
      {children}
    </div>
  </div>
);

export const AccessDenied: React.FC = () => (
  <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Integrations</p>
    <p className="mt-1 text-sm text-gray-500">Ask an administrator for the integration.view permission.</p>
  </div>
);
